<template>
  <!-- Loading state -->
  <div v-if="isLoading" class="loading-container">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Main content after loading -->
  <template v-else-if="currentUser">
    <header>
      <nav class="navbar navbar-light navbar-expand-lg fixed-top">
        <div class="container-fluid">
          <AppBrand :app-env="appEnv" />

          <!-- Menu icons -->
          <MenuIcons :current-user="currentUser" @toggle-user-menu="toggleUserMenu" @toggle-app-menu="toggleAppMenu" />
        </div>
      </nav>
    </header>

    <!-- Menus -->
    <UserMenu :is-open="isUserMenuOpen" :current-user="currentUser" @close="closeUserMenu" />
    <AppMenu :is-open="isAppMenuOpen" @close="closeAppMenu" />

    <!-- Side Menu -->
    <SideMenu />

    <main class="main-content">
      <RouterView :key="route.path"/>
    </main>

    <Footer />

    <!-- Language Switcher FAB -->
    <LanguageSwitcher />
  </template>
</template>

<script setup>
import {onMounted, ref, onUnmounted} from 'vue';
import {useRoute} from 'vue-router';
import {useAuthUser} from '../../composables/useAuthUser.js';
import AppBrand from '../../components/user/layout/AppBrand.vue';
import MenuIcons from '../../components/user/layout/MenuIcons.vue';
import UserMenu from '../../components/user/layout/UserMenu.vue';
import AppMenu from '../../components/user/layout/AppMenu.vue';
import SideMenu from '../../components/user/layout/SideMenu.vue';
import Footer from './Footer.vue';
import LanguageSwitcher from '../../components/common/LanguageSwitcher.vue';
import { colors } from '../../../consts/colors';

const route = useRoute();
const {fetchCurrentUser, currentUser} = useAuthUser();

// Get app environment
const appEnv = import.meta.env.VITE_APP_ENV;

// Mobile menu states
const isUserMenuOpen = ref(false);
const isAppMenuOpen = ref(false);

// Loading state
const isLoading = ref(true);

onMounted(async () => {
  try {
    await fetchCurrentUser();
  } catch (error) {
    console.error('Failed to fetch current user:', error);
  } finally {
    isLoading.value = false;
  }

  // Add event listener for clicking outside menus
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  // Clean up event listener
  document.removeEventListener('click', handleClickOutside);
});

/**
 * Handle clicks outside of menus
 */
const handleClickOutside = (event) => {
  const userMenu = document.querySelector('.user-menu');
  const appMenu = document.querySelector('.mobile-app-menu');
  const userMenuIcon = document.querySelector('.menu-icons .btn-user-menu');
  const appMenuIcon = document.querySelector('.menu-icons .btn-icon');

  // Close user menu if clicked outside
  if (isUserMenuOpen.value && userMenu && !userMenu.contains(event.target) && !userMenuIcon?.contains(event.target)) {
    closeUserMenu();
  }

  // Close app menu if clicked outside
  if (isAppMenuOpen.value && appMenu && !appMenu.contains(event.target) && !appMenuIcon?.contains(event.target)) {
    closeAppMenu();
  }
};

/**
 * Toggle mobile user menu
 */
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
  if (isUserMenuOpen.value) {
    isAppMenuOpen.value = false; // Close app menu if open
    // Prevent body scroll when menu is open
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

/**
 * Toggle mobile app menu
 */
const toggleAppMenu = () => {
  isAppMenuOpen.value = !isAppMenuOpen.value;
  if (isAppMenuOpen.value) {
    isUserMenuOpen.value = false; // Close user menu if open
    // Prevent body scroll when menu is open
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

/**
 * Close mobile user menu
 */
const closeUserMenu = () => {
  isUserMenuOpen.value = false;
  document.body.style.overflow = '';
};

/**
 * Close mobile app menu
 */
const closeAppMenu = () => {
  isAppMenuOpen.value = false;
  document.body.style.overflow = '';
};

</script>


<style lang="scss" scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: v-bind('colors.background');
}

button {
  color: v-bind('colors.buttonBlue');
  background-color: transparent;
  border-color: v-bind('colors.borderGray');
  border-radius: 50px;
}

header {
  color: v-bind('colors.textPrimary');
  background-color: v-bind('colors.background');

  .navbar {
    background-color: v-bind('colors.background');
  }

  .nav-link {
    color: v-bind('colors.textPrimary');
  }
}

.main-content {
  margin-left: 250px;
  padding-top: 54px;
  min-height: 100vh;
  background: v-bind('colors.background');

  // Remove left margin on mobile
  @media (max-width: 991.98px) {
    margin-left: 0;
  }
}
</style>
